/**
 * Inside Sales - rep agency browse (master-detail layout like Customers).
 */

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { RepAgencyDetailPanel, type AgencyDetailTab } from "@/components/inside-sales/RepAgencyDetailPanel";
import { Badge } from "@/components/ui/Badge";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Input } from "@/components/ui/Input";
import type { SortDirection } from "@/components/ui/SortableTableHeader";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { cn } from "@/lib/utils";

interface RepAgencyListRow {
  id: number;
  name: string;
  fs_account_id: string | null;
  fs_account_name: string | null;
  mv_supplier_id: string | null;
  mv_supplier_name: string | null;
  mv_supplier_type: string | null;
  mv_client_id: string | null;
  mv_client_name: string | null;
  commission_terminated_at: string | null;
  integration: {
    has_freshsales_id: boolean;
    has_megaventory_supplier: boolean;
    has_megaventory_customer: boolean;
    megaventory_supplier_is_both: boolean;
    name_matches_supplier: boolean;
    name_matches_freshsales: boolean;
  };
}

interface RepPersonRow {
  staff_id: number | null;
  display_name: string;
  email: string | null;
  agency_id: number;
  agency_name: string;
  commission_terminated_at: string | null;
}

type RepListView = "agencies" | "reps";

const SEARCH_DEBOUNCE_MS = 400;

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

export function RepAgenciesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const agencyIdParam = searchParams.get("agencyId") ?? "";
  const staffIdParam = searchParams.get("staffId") ?? "";
  const [searchText, setSearchText] = useState("");
  const [listView, setListView] = useState<RepListView>("agencies");
  const [detailTab, setDetailTab] = useState<AgencyDetailTab>("information");
  const [showTerminated, setShowTerminated] = useState(false);
  const [agencySortDir, setAgencySortDir] = useState<SortDirection>("asc");
  const [repSortDir, setRepSortDir] = useState<SortDirection>("asc");
  const debouncedSearch = useDebouncedValue(searchText.trim(), SEARCH_DEBOUNCE_MS);

  const selectedAgencyId = useMemo(() => {
    const parsed = Number.parseInt(agencyIdParam.trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [agencyIdParam]);
  const selectedStaffId = useMemo(() => {
    const parsed = Number.parseInt(staffIdParam.trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [staffIdParam]);

  const listQuery = useQuery<{ rows: RepAgencyListRow[] }, ApiError>({
    queryKey: ["inside-sales", "rep-agencies"],
    queryFn: () => apiFetch<{ rows: RepAgencyListRow[] }>("/api/inside-sales/rep-agencies"),
  });

  const peopleQuery = useQuery<{ rows: RepPersonRow[] }, ApiError>({
    queryKey: ["inside-sales", "rep-people"],
    queryFn: () => apiFetch<{ rows: RepPersonRow[] }>("/api/inside-sales/rep-people"),
  });

  const allRows = listQuery.data?.rows ?? [];
  const allPeople = peopleQuery.data?.rows ?? [];
  const normalizedSearch = debouncedSearch.trim().toLowerCase();
  const peopleByAgencyId = useMemo(() => {
    const grouped = new Map<number, RepPersonRow[]>();
    for (const person of allPeople) {
      const current = grouped.get(person.agency_id) ?? [];
      current.push(person);
      grouped.set(person.agency_id, current);
    }
    return grouped;
  }, [allPeople]);
  const rows = useMemo(() => {
    return allRows.filter((row) => {
      const terminated = row.commission_terminated_at != null && row.commission_terminated_at.trim() !== "";
      if (!showTerminated && terminated && row.id !== selectedAgencyId) {
        return false;
      }
      if (normalizedSearch === "") {
        return true;
      }
      const peopleText = (peopleByAgencyId.get(row.id) ?? [])
        .map((person) => `${person.display_name} ${person.email ?? ""}`)
        .join(" ");
      const haystack = [row.name, row.fs_account_name, row.mv_supplier_name, row.mv_client_name, peopleText]
        .map((value) => (value == null ? "" : value.toLowerCase()))
        .join(" ");
      return haystack.includes(normalizedSearch);
    });
  }, [allRows, normalizedSearch, peopleByAgencyId, selectedAgencyId, showTerminated]);
  const repRows = useMemo(() => {
    return allPeople.filter((person) => {
      const terminated = person.commission_terminated_at != null && person.commission_terminated_at.trim() !== "";
      if (!showTerminated && terminated) {
        return false;
      }
      if (normalizedSearch === "") {
        return true;
      }
      const haystack = [person.display_name, person.email, person.agency_name]
        .map((value) => (value == null ? "" : value.toLowerCase()))
        .join(" ");
      return haystack.includes(normalizedSearch);
    }).sort((left, right) => left.display_name.localeCompare(right.display_name) || left.agency_name.localeCompare(right.agency_name));
  }, [allPeople, normalizedSearch, showTerminated]);

  const selectedListRow = allRows.find((row) => row.id === selectedAgencyId) ?? null;
  const selectedPerson = allPeople.find(
    (person) => person.staff_id === selectedStaffId && person.agency_id === selectedAgencyId,
  ) ?? null;
  const breadcrumbLabel = selectedPerson?.display_name ?? selectedListRow?.name ?? "Rep agencies";

  function openAgency(agencyId: number): void {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("agencyId", String(agencyId));
      next.delete("staffId");
      return next;
    });
  }

  function openStaff(agencyId: number, staffId: number): void {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set("agencyId", String(agencyId));
      next.set("staffId", String(staffId));
      return next;
    });
  }

  const listLoading = listView === "agencies" ? listQuery.isLoading : peopleQuery.isLoading;
  const listError = listView === "agencies" ? listQuery.error : peopleQuery.error;

  const agencyColumns = useMemo((): HubDataTableColumn<RepAgencyListRow, "name">[] => {
    return [
      {
        id: "name",
        header: "Agency",
        sortKey: "name",
        sortValue: (row) => row.name,
        cell: (row) => (
          <span className="inline-flex flex-wrap items-center gap-2 text-xs font-medium text-slate-900">
            {row.name}
            {row.commission_terminated_at ? <Badge tone="danger">Terminated</Badge> : null}
          </span>
        ),
      },
    ];
  }, []);

  const repColumns = useMemo((): HubDataTableColumn<RepPersonRow, "display_name">[] => {
    return [
      {
        id: "display_name",
        header: "Rep",
        sortKey: "display_name",
        sortValue: (row) => row.display_name,
        cell: (row) => (
          <span className="inline-flex w-full items-center justify-between gap-2 text-xs">
            <span className="font-medium text-slate-900">{row.display_name}</span>
            <Badge tone="neutral">{row.agency_name}</Badge>
          </span>
        ),
      },
    ];
  }, []);

  const sortedAgencyRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((left, right) => {
      const compared = left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
      return agencySortDir === "asc" ? compared : -compared;
    });
    return copy;
  }, [agencySortDir, rows]);

  const sortedRepRows = useMemo(() => {
    const copy = [...repRows];
    copy.sort((left, right) => {
      const compared =
        left.display_name.localeCompare(right.display_name, undefined, { sensitivity: "base" }) ||
        left.agency_name.localeCompare(right.agency_name, undefined, { sensitivity: "base" });
      return repSortDir === "asc" ? compared : -compared;
    });
    return copy;
  }, [repRows, repSortDir]);

  const filtersActive = normalizedSearch !== "" || showTerminated;

  return (
    <div className="space-y-4">
      <PageTitleWithHelp
        hoverHint="Browse rep agencies with Freshsales account and Megaventory supplier links. Select a rep to see commission settings, integration status, and related sales orders."
        breadcrumbLabel={breadcrumbLabel}
      >
        Rep agencies
      </PageTitleWithHelp>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex flex-wrap items-end gap-x-8 gap-y-4">
            <div className="min-w-[16rem] max-w-xl flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-700" htmlFor="rep-agency-search">
                Search
              </label>
              <Input
                id="rep-agency-search"
                type="search"
                className="h-10 w-full"
                placeholder="Agency, rep name, or email"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
              />
            </div>
            <button
              type="button"
              aria-pressed={showTerminated}
              className={cn(
                "inline-flex h-10 items-center rounded-full border px-4 text-sm font-medium",
                showTerminated
                  ? "border-red-300 bg-red-100 text-red-800"
                  : "border-slate-300 bg-white text-slate-700",
              )}
              onClick={() => setShowTerminated((current) => !current)}
            >
              Terminated
            </button>
          </div>
        </CardBody>
      </Card>

      <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[minmax(20rem,26rem)_minmax(0,1fr)] lg:items-stretch">
        {listView === "agencies" ? (
          <HubDataTable
            className="min-h-[28rem]"
            headerExtra={
              <div className="relative w-full min-w-[16rem]">
                <SegmentedControl
                  fill
                  aria-label="Rep list"
                  value={listView}
                  onChange={setListView}
                  options={[
                    { value: "agencies", label: "Rep agencies" },
                    { value: "reps", label: "Reps" },
                  ]}
                />
                {listQuery.isFetching ? (
                  <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-500" aria-hidden />
                ) : null}
              </div>
            }
            columns={agencyColumns}
            rows={sortedAgencyRows}
            rowKey={(row) => row.id}
            sortKey="name"
            sortDir={agencySortDir}
            onSort={() => setAgencySortDir((current) => (current === "asc" ? "desc" : "asc"))}
            loading={listLoading}
            loadingMessage="Loading..."
            error={listError?.message ?? null}
            empty="No rep agencies found."
            isFiltered={filtersActive}
            filteredEmpty="No rep agencies match this search."
            onRowClick={(row) => openAgency(row.id)}
            rowClassName={(row) =>
              cn(selectedAgencyId === row.id && "bg-sky-50 ring-1 ring-inset ring-sky-200")
            }
            tableClassName="w-full table-fixed border-collapse text-left text-xs text-slate-800"
          />
        ) : (
          <HubDataTable
            className="min-h-[28rem]"
            headerExtra={
              <div className="relative w-full min-w-[16rem]">
                <SegmentedControl
                  fill
                  aria-label="Rep list"
                  value={listView}
                  onChange={setListView}
                  options={[
                    { value: "agencies", label: "Rep agencies" },
                    { value: "reps", label: "Reps" },
                  ]}
                />
                {peopleQuery.isFetching ? (
                  <Loader2 className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-slate-500" aria-hidden />
                ) : null}
              </div>
            }
            columns={repColumns}
            rows={sortedRepRows}
            rowKey={(person) => `${person.agency_id}-${person.staff_id ?? person.email ?? person.display_name}`}
            sortKey="display_name"
            sortDir={repSortDir}
            onSort={() => setRepSortDir((current) => (current === "asc" ? "desc" : "asc"))}
            loading={listLoading}
            loadingMessage="Loading..."
            error={listError?.message ?? null}
            empty="No reps found."
            isFiltered={filtersActive}
            filteredEmpty="No reps match this search."
            onRowClick={(person) => {
              if (person.staff_id != null) {
                openStaff(person.agency_id, person.staff_id);
              } else {
                openAgency(person.agency_id);
              }
            }}
            rowClassName={(person) =>
              cn(
                selectedStaffId === person.staff_id &&
                  selectedAgencyId === person.agency_id &&
                  "bg-sky-50 ring-1 ring-inset ring-sky-200",
              )
            }
            tableClassName="w-full table-fixed border-collapse text-left text-xs text-slate-800"
          />
        )}

        <Card className="flex min-h-0 flex-col">
          {selectedAgencyId == null ? (
            <CardBody className="flex flex-1 flex-col justify-center">
              <p className="text-sm text-slate-600">
                Select a rep agency from the list to see integration links, commission settings, and related sales
                orders.
              </p>
            </CardBody>
          ) : (
            <>
              <CardHeader className="flex shrink-0 flex-row items-center gap-4 px-5 py-3">
                <CardTitle className="shrink-0">{selectedPerson?.display_name ?? selectedListRow?.name ?? "Details"}</CardTitle>
                {selectedStaffId == null ? (
                  <div className="min-w-0 flex-1">
                    <SegmentedControl
                      fill
                      aria-label="Agency detail"
                      value={detailTab}
                      onChange={setDetailTab}
                      options={[
                        { value: "information", label: "Information" },
                        { value: "staff", label: "Staff" },
                        { value: "sales-orders", label: "Sales orders" },
                      ]}
                    />
                  </div>
                ) : null}
              </CardHeader>
              <CardBody className="min-h-0 flex-1 overflow-y-auto">
                <RepAgencyDetailPanel
                  agencyId={selectedAgencyId}
                  staffId={selectedStaffId}
                  tab={detailTab}
                  onOpenStaff={(staffId) => openStaff(selectedAgencyId, staffId)}
                />
              </CardBody>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
